import React from 'react';

import { AtlassianIcon, ConfluenceIcon, JiraIcon, OpsgenieIcon } from '@atlaskit/logo';
import type { AuditLogEventActor } from '@atlassian/audit-logs-common';

import Avatar from '../../../../../common/utils/components/avatar';

/**
 * Checks if the actor is a system actor based on email property
 * If the email does not contain an @ sign, it is considered a system actor
 */
export const isSystemActor = (actor?: AuditLogEventActor): boolean => {
	if (!actor?.email) {
		return false;
	}

	// If email does not contain @ sign, it's a system actor
	return !actor.email.includes('@');
};

const defaultLogoAppearance = 'brand';

/**
 * Renders a logo for a given actor based on actor accountId, with a fallback to Atlassian icon
 * Wrapped with proper spacing to match Avatar component alignment
 */
export const getProductLogo = (actor?: AuditLogEventActor): React.JSX.Element => {
	if (!actor?.accountId) {
		return <Avatar size="small" src={undefined} />;
	}

	const actorId = actor.accountId.toLowerCase();
	let logo;

	switch (actorId) {
		case 'atlassian-support':
			logo = (
				<AtlassianIcon appearance={defaultLogoAppearance} size="small" shouldUseNewLogoDesign />
			);
			break;
		case 'atlassian-internal-system':
			logo = (
				<AtlassianIcon appearance={defaultLogoAppearance} size="small" shouldUseNewLogoDesign />
			);
			break;
		case 'opsgenie-user-service':
			logo = (
				<OpsgenieIcon appearance={defaultLogoAppearance} size="small" shouldUseNewLogoDesign />
			);
			break;
		case 'jira':
			logo = <JiraIcon appearance={defaultLogoAppearance} size="small" shouldUseNewLogoDesign />;
			break;
		case 'confluence':
			logo = (
				<ConfluenceIcon appearance={defaultLogoAppearance} size="small" shouldUseNewLogoDesign />
			);
			break;
		case 'anonymous':
		case 'unidentified':
		case 'idp':
			// Show avatar for anonymous, unidentified, and idp actors
			return <Avatar size="small" src={undefined} />;
		default:
			// Default fallback for unknown system actors
			return <Avatar size="small" src={undefined} />;
	}

	return logo;
};
